import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Users2, Building2, MapPin, BadgeCheck, Boxes, Search, Plus, X, Loader2, Save,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Chip input with native Apollo-backed autocomplete (mirrors the targeting editor).
const ChipField = ({ label, icon: Icon, items, onChange, placeholder, field, testid }) => {
  const [draft, setDraft] = useState('');
  const [sugs, setSugs] = useState([]);
  useEffect(() => {
    if (!field) return;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`${API}/api/brain/icp/suggest`, { params: { field, q: draft } });
        setSugs(r.data?.suggestions || []);
      } catch { setSugs([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [draft, field]);
  const add = (val) => {
    const v = (val ?? draft).trim();
    if (!v) return;
    if (!items.includes(v)) onChange([...items, v]);
    setDraft('');
  };
  return (
    <div data-testid={`seg-edit-${testid}`}>
      <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" /> {label}
      </p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border bg-primary/10 text-primary border-primary/20"
            data-testid={`seg-chip-${testid}-${i}`}>
            {it}
            <button onClick={() => onChange(items.filter((_, idx) => idx !== i))}
              className="hover:text-red-500 transition-colors" data-testid={`seg-chip-remove-${testid}-${i}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">None yet</span>}
      </div>
      <div className="flex gap-1.5">
        <input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder} data-testid={`seg-input-${testid}`} list={`segdl-${testid}`}
          className="flex-1 rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary" />
        <datalist id={`segdl-${testid}`}>{sugs.map((s, i) => <option key={i} value={s} />)}</datalist>
        <button onClick={() => add()} data-testid={`seg-add-${testid}`}
          className="inline-flex items-center px-2 rounded-lg border border-border hover:bg-muted transition-colors">
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

// Edit a segment's targeting chips — lets users build a brand-new audience from
// scratch (blank segment) instead of only cloning current targeting.
export const ICPSegmentEditor = ({ segment, open, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [titles, setTitles] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [seniorities, setSeniorities] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!segment) return;
    const c = segment.chips || {};
    setName(segment.name || '');
    setTitles(c.titles || []);
    setIndustries(c.industries || []);
    setLocations(c.locations || []);
    setSeniorities(c.seniorities || []);
    setCompanySizes(c.company_sizes || []);
    setKeyword(c.keyword || '');
  }, [segment]);

  const save = async () => {
    if (!name.trim()) { toast.error('Give the segment a name'); return; }
    setSaving(true);
    try {
      await api.put(`${API}/api/brain/icp/segments/${segment.id}`, {
        name: name.trim(),
        chips: {
          titles, industries, locations, seniorities,
          company_sizes: companySizes, keyword,
        },
      });
      toast.success(`"${name.trim()}" saved`);
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save segment');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="icp-segment-editor-dialog">
        <DialogHeader>
          <DialogTitle>Edit segment targeting</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Segment name</p>
            <input value={name} onChange={(e) => setName(e.target.value)}
              data-testid="seg-edit-name"
              className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
            <ChipField label="Target titles" icon={Users2} items={titles} field="titles"
              onChange={setTitles} placeholder="e.g. VP Sales" testid="titles" />
            <ChipField label="Industries" icon={Building2} items={industries} field="industries"
              onChange={setIndustries} placeholder="e.g. SaaS" testid="industries" />
            <ChipField label="Locations" icon={MapPin} items={locations} field="locations"
              onChange={setLocations} placeholder="e.g. United States" testid="locations" />
            <ChipField label="Seniorities" icon={BadgeCheck} items={seniorities} field="seniorities"
              onChange={setSeniorities} placeholder="e.g. c_suite, vp" testid="seniorities" />
            <ChipField label="Company sizes" icon={Boxes} items={companySizes} field="company_sizes"
              onChange={setCompanySizes} placeholder="e.g. 11-50" testid="company-sizes" />
            <div data-testid="seg-edit-keyword">
              <p className="text-xs font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                <Search className="w-3.5 h-3.5" /> Keyword (optional)
              </p>
              <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
                placeholder="Leave blank for the widest pool" data-testid="seg-input-keyword"
                className="w-full rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} data-testid="seg-edit-cancel"
            className="text-sm px-3 py-2 rounded-lg border border-border hover:bg-muted transition-colors">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="seg-edit-save"
            className="inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save segment
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ICPSegmentEditor;
